import { createReducer, on } from '@ngrx/store';

import { updateStoreAction } from '@fcom/core/store/init-store.action';
import { Category, FinnairPassengerServiceItem, FinnairServiceItem, SeatInfo } from '@fcom/dapi/api/models';
import { FlightSeatSelections, SeatMapState, SeatMapStatus } from '@fcom/common/interfaces/seat-map.interface';
import { SeatCharacteristics } from '@fcom/dapi/interfaces';
import { compareAsJson, DataUtils } from '@fcom/core/utils';
import { CartActions } from '@fcom/booking/store/actions';
import { BookingAppState } from '@fcom/common/interfaces/booking';

import { OrderActions, SeatMapActions } from '../actions';
import { combineIncludedAndUnpaidSeats } from '../../modules/ancillaries/utils';
import { COMMON_BOOKING_FEATURE_KEY } from '../constants';
import { CommonBookingFeatureState } from '../store.interface';
import { onSeatMapFlowReset } from './seat-map-flow-reset.on';

const ROOT_SEATMAPS_KEY = 'seatmaps';

export const initialState: SeatMapState = Object.seal({ [ROOT_SEATMAPS_KEY]: {}, originalRegularSeatByFlight: {} });

export const setSeatSelectionForFlight = (
  state: SeatMapState | undefined,
  flightId: string,
  travelerId: string,
  seat: SeatInfo | null
): SeatMapState => {
  const offerItem = seat?.prices?.find((price) => price.travelerIds.includes(travelerId));
  return DataUtils.wrap(state)
    .updateIn([ROOT_SEATMAPS_KEY, flightId, 'selections'], (selections: FlightSeatSelections) => {
      // Only an actual seat number can conflict, so clearing a seat leaves seatless travelers alone.
      if (!selections || !seat?.seatNumber) {
        return selections ?? {};
      }

      return Object.keys(selections)
        .filter(
          (innerTravelerId) =>
            innerTravelerId !== travelerId && selections[innerTravelerId]?.seatNumber === seat.seatNumber
        )
        .reduce(
          (updatedSelections, conflictingId) =>
            DataUtils.wrap(updatedSelections)
              .setIn([conflictingId, 'seatNumber'], null)
              .setIn([conflictingId, 'seatType'], null)
              .setIn([conflictingId, 'characteristics'], null)
              .value(),
          selections
        );
    })
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'seatNumber'], seat?.seatNumber || null)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'seatType'], seat?.type || null)
    .setIn(
      [ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'characteristics'],
      (seat?.characteristics as SeatCharacteristics[]) || null
    )
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'travelerId'], travelerId)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'paid'], false)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'ticketed'], false)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'modifiable'], true)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'offer'], offerItem?.offerItem ?? null)
    .setIn([ROOT_SEATMAPS_KEY, flightId, 'selections', travelerId, 'pricePerItem'], offerItem?.price)
    .value();
};

const flightServices = (service: FinnairServiceItem | undefined, flightId: string) => {
  if (!service) {
    return [];
  }
  return [
    ...service.bounds.reduce(
      (allServices, b) => allServices.concat(...(b.segments.find((s) => s.id === flightId)?.passengers || [])),
      []
    ),
  ];
};

const toFlightSeatSelections = (
  seatSelectionsInCart: FinnairServiceItem,
  flightId: string,
  includedSeats: FinnairPassengerServiceItem[],
  setPaidStatus: boolean
): FlightSeatSelections =>
  flightServices(seatSelectionsInCart, flightId).reduce(
    (acc: FlightSeatSelections, service: FinnairPassengerServiceItem) => {
      const seatService = service.services.find((s) => !!s.seatNumber) || service.services[0];
      acc[service.id] = {
        seatNumber: seatService.seatNumber,
        seatType: seatService.seatType,
        characteristics: seatService.characteristics as SeatCharacteristics[],
        paid: setPaidStatus
          ? includedSeats.some((includedSeat) => compareAsJson(includedSeat, service)) &&
            parseFloat(seatService.totalPrice?.amount) > 0
          : false,
        modifiable: seatService.modifiable ?? true,
        ticketed: seatService.ticketed ?? false,
        travelerId: service.id,
        pricePerItem: seatService.unitPrice,
      };
      return acc;
    },
    {}
  );

const loadSeatSelections = function (
  state: SeatMapState,
  seatSelections: FinnairServiceItem | undefined,
  includedSeats: FinnairServiceItem[],
  setPaidStatus: boolean
) {
  const clearedSelectionsAndLoadingStatus = Object.keys(state[ROOT_SEATMAPS_KEY]).reduce(
    (s: SeatMapState, currentFlightId: string) => {
      return DataUtils.wrap(s)
        .deleteIn([ROOT_SEATMAPS_KEY, currentFlightId, 'selections'])
        .updateIn([ROOT_SEATMAPS_KEY, currentFlightId, 'status'], (status: SeatMapStatus) =>
          status === SeatMapStatus.PENDING ? SeatMapStatus.INITIAL : status
        )
        .value();
    },
    state
  );
  return (
    seatSelections?.bounds.reduce((s: SeatMapState, currentBound) => {
      return currentBound.segments.reduce((s2: SeatMapState, currentSegment) => {
        const newSelections = toFlightSeatSelections(
          seatSelections,
          currentSegment.id,
          includedSeats.flatMap((service) =>
            service.bounds
              .filter((bound) => bound.id === currentBound.id)
              .flatMap((bound) =>
                bound.segments
                  .filter((segment) => segment.id === currentSegment.id)
                  .flatMap((segment) => segment.passengers)
              )
          ),
          setPaidStatus
        );
        return (
          DataUtils.wrap(s2)
            // Set default value for seat map status
            .updateIn(
              [ROOT_SEATMAPS_KEY, currentSegment.id, 'status'],
              (status: SeatMapStatus) => status || SeatMapStatus.INITIAL
            )
            .setIn([ROOT_SEATMAPS_KEY, currentSegment.id, 'selections'], newSelections)
            .value()
        );
      }, s);
    }, clearedSelectionsAndLoadingStatus) || clearedSelectionsAndLoadingStatus
  );
};

function isCart(payload: BookingAppState | CommonBookingFeatureState): payload is BookingAppState {
  return !!(payload as BookingAppState)?.cart?.cartData;
}

function getServices(payload: BookingAppState | CommonBookingFeatureState): FinnairServiceItem[] {
  return isCart(payload)
    ? payload?.cart?.cartData?.services.unpaid
    : combineIncludedAndUnpaidSeats(
        payload?.commonBooking?.order?.orderData?.services?.included || [],
        payload?.commonBooking?.order?.orderData?.services?.unpaid || []
      );
}

export const seatMapReducer = createReducer(
  initialState,
  on(updateStoreAction, (state, { payload, features }) => {
    if (features.includes(COMMON_BOOKING_FEATURE_KEY)) {
      const services: FinnairServiceItem[] = getServices(payload);
      return loadSeatSelections(
        state,
        services?.find((s) => s.category === Category.SEAT),
        !isCart(payload)
          ? ((payload as CommonBookingFeatureState)?.commonBooking?.order?.orderData?.services?.included ?? []).filter(
              (s) => s.category === Category.SEAT
            )
          : [],
        !isCart(payload)
      );
    }
    return state;
  }),
  on(OrderActions.setOrderData, (state, { order, keepSeatsData }) => {
    const services: FinnairServiceItem[] = combineIncludedAndUnpaidSeats(
      order?.services?.included ?? [],
      order?.services?.unpaid ?? []
    );
    return loadSeatSelections(
      keepSeatsData ? state : initialState,
      services?.find((s) => s.category === Category.SEAT),
      (order?.services?.included ?? []).filter((s) => s.category === Category.SEAT),
      true
    );
  }),
  on(SeatMapActions.resetSeatSelections, (state, { cartOrOrder }) => {
    const seatServices: FinnairServiceItem = combineIncludedAndUnpaidSeats(
      cartOrOrder?.services?.included ?? [],
      cartOrOrder?.services?.unpaid ?? []
    )?.find((s) => s.category === Category.SEAT);
    const includedSeatServices: FinnairServiceItem[] = (cartOrOrder?.services?.included ?? [])?.filter(
      (s) => s.category === Category.SEAT
    );
    return loadSeatSelections(state, seatServices, includedSeatServices, true);
  }),
  on(SeatMapActions.startLoading, (state, { flightId }) => {
    return DataUtils.wrap(state).mergeIn([ROOT_SEATMAPS_KEY, flightId], { status: SeatMapStatus.PENDING }).value();
  }),
  on(SeatMapActions.error, (state, { flightId }) => {
    return DataUtils.wrap(state).setIn([ROOT_SEATMAPS_KEY, flightId], { status: SeatMapStatus.LOAD_ERROR }).value();
  }),
  on(SeatMapActions.clearAllSeats, (state, { travelerIds }) => {
    return Object.keys(state[ROOT_SEATMAPS_KEY]).reduce(
      (newState, flightId) => {
        const selections: FlightSeatSelections = state[ROOT_SEATMAPS_KEY][flightId]?.selections;
        if (!selections) {
          return {
            ...newState,
            [ROOT_SEATMAPS_KEY]: {
              ...state[ROOT_SEATMAPS_KEY],
              [flightId]: { ...state[ROOT_SEATMAPS_KEY][flightId], selections: selections },
            },
          };
        }
        const updatedSelections = travelerIds.reduce(
          (acc: FlightSeatSelections, travelerId: string) => {
            if (acc[travelerId]) {
              return {
                ...acc,
                [travelerId]: { ...acc[travelerId], seatNumber: null, seatType: null, characteristics: null },
              };
            }
            return acc;
          },
          { ...selections }
        );
        const updatedOriginalRegularSeatByFlight = travelerIds.reduce(
          (acc, travelerId) =>
            acc[flightId]?.[travelerId] ? DataUtils.wrap(acc).setIn([flightId, travelerId], false).value() : acc,
          newState.originalRegularSeatByFlight
        );
        return {
          ...newState,
          originalRegularSeatByFlight: updatedOriginalRegularSeatByFlight,
          [ROOT_SEATMAPS_KEY]: {
            ...state[ROOT_SEATMAPS_KEY],
            [flightId]: { ...state[ROOT_SEATMAPS_KEY][flightId], selections: updatedSelections },
          },
        };
      },
      {
        latestToken: state.latestToken,
        originalRegularSeatByFlight: state.originalRegularSeatByFlight ?? {},
      } as SeatMapState
    );
  }),
  on(SeatMapActions.setSeatForFlight, (state, { flightId, travelerId, seat }) => {
    const newState = setSeatSelectionForFlight(state, flightId, travelerId, seat);

    return {
      ...newState,
      originalRegularSeatByFlight: {
        ...newState.originalRegularSeatByFlight,
        [flightId]: {
          ...newState.originalRegularSeatByFlight?.[flightId],
          [travelerId]: !!seat?.seatNumber,
        },
      },
    };
  }),
  on(SeatMapActions.setSeatMap, (state, { seatMapForFlight }) => {
    return DataUtils.wrap(state)
      .setIn(['latestToken'], seatMapForFlight.seatMap.token)
      .mergeIn([ROOT_SEATMAPS_KEY, seatMapForFlight.flightId], {
        status: SeatMapStatus.OK,
        seatMap: seatMapForFlight.seatMap,
      })
      .value();
  }),
  on(SeatMapActions.loadSeatSelectionsFromCart, CartActions.setCartData, (state, { cartData }) => {
    return loadSeatSelections(
      state,
      cartData?.services.unpaid?.find((s) => s.category === Category.SEAT),
      [],
      false
    );
  }),
  on(SeatMapActions.clearSelections, (state) => {
    return {
      ...state,
      [ROOT_SEATMAPS_KEY]: Object.keys(state[ROOT_SEATMAPS_KEY]).reduce((newState, flightId) => {
        return DataUtils.wrap(newState).deleteIn([flightId, 'selections']).value();
      }, state[ROOT_SEATMAPS_KEY]),
    };
  }),
  onSeatMapFlowReset(initialState)
);
